/**
 * SmeadITWidget — Bug Report Form (KO Holistic Health website)
 * Submits tickets to SmeadIT's Firebase (smeadit-1fab6) as a secondary app
 * instance. KO's own Firebase is completely unaffected.
 */

import { useState, useRef } from 'react';
import {
  collection, addDoc, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { smeaditDb, smeaditStorage, SMEADIT_SITE_ID, SMEADIT_CLIENT_ID } from './smeaditFirebase';
import './SmeadITWidget.css';

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const BUG_TYPES = [
  { value: '', label: 'Select type...' },
  { value: 'ui', label: 'UI / Visual' },
  { value: 'functionality', label: 'Functionality' },
  { value: 'performance', label: 'Performance' },
  { value: 'data', label: 'Data / Content' },
  { value: 'other', label: 'Other' },
];

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const EMPTY_FORM = {
  title: '',
  description: '',
  pageAffected: '',
  priority: 'medium',
  bugType: '',
};

function NotConfigured() {
  return (
    <div className="smeadit-notice smeadit-notice--warn">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      <div>
        <strong>Bug reporting not configured yet.</strong>
        <p>Please contact SmeadIT to activate this feature.</p>
      </div>
    </div>
  );
}

const SmeadITWidget = () => {
  const notConfigured =
    !SMEADIT_SITE_ID ||
    SMEADIT_SITE_ID.startsWith('PASTE_');

  const [form, setForm] = useState(EMPTY_FORM);
  const [screenshot, setScreenshot] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const fileRef = useRef();

  if (notConfigured) return <NotConfigured />;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, screenshot: 'Only image files are accepted.' }));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setErrors(prev => ({ ...prev, screenshot: 'File must be under 10MB.' }));
      return;
    }
    setErrors(prev => ({ ...prev, screenshot: null }));
    setScreenshot({ file, preview: URL.createObjectURL(file) });
  };

  const removeScreenshot = () => {
    if (screenshot?.preview) URL.revokeObjectURL(screenshot.preview);
    setScreenshot(null);
    setUploadProgress(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const uploadScreenshot = () =>
    new Promise((resolve, reject) => {
      const { file } = screenshot;
      const path = `screenshots/${SMEADIT_SITE_ID}/client_${Date.now()}_${file.name}`;
      const storageRef = ref(smeaditStorage, path);
      const task = uploadBytesResumable(storageRef, file);
      task.on(
        'state_changed',
        snap => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        reject,
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve({ url, path });
        }
      );
    });

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Title is required.';
    if (!form.description.trim()) e.description = 'Description is required.';
    if (!form.pageAffected.trim()) e.pageAffected = 'Please specify which page is affected.';
    if (!form.priority) e.priority = 'Priority is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      let screenshotUrl = null;
      let screenshotPath = null;
      if (screenshot?.file) {
        const result = await uploadScreenshot();
        screenshotUrl = result.url;
        screenshotPath = result.path;
      }

      const ticketRef = await addDoc(
        collection(smeaditDb, 'monitoredSites', SMEADIT_SITE_ID, 'tickets'),
        {
          type: 'bug',
          submittedBy: 'client',
          clientId: SMEADIT_CLIENT_ID,
          title: form.title.trim(),
          description: form.description.trim(),
          pageAffected: form.pageAffected.trim(),
          priority: form.priority,
          bugType: form.bugType || null,
          screenshotUrl,
          screenshotPath,
          status: 'open',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
      );

      setSuccess(`Your report has been submitted (ref #${ticketRef.id.slice(-6).toUpperCase()}). Thank you!`);
      setForm(EMPTY_FORM);
      removeScreenshot();
      setUploadProgress(null);
      setTimeout(() => setSuccess(null), 8000);
    } catch (err) {
      console.error('SmeadIT: failed to submit bug', err);
      setErrors(prev => ({ ...prev, _global: 'Failed to submit. Please try again shortly.' }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="smeadit-widget">
      {success && (
        <div className="smeadit-notice smeadit-notice--success">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          <span>{success}</span>
        </div>
      )}
      {errors._global && (
        <div className="smeadit-notice smeadit-notice--error">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span>{errors._global}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="smeadit-form-grid">
          <div className="smeadit-field">
            <label className="smeadit-label">
              Title <span className="smeadit-required">*</span>
            </label>
            <input
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Brief summary of the issue"
              className={`smeadit-input ${errors.title ? 'smeadit-input--error' : ''}`}
            />
            {errors.title && <span className="smeadit-error">{errors.title}</span>}
          </div>

          <div className="smeadit-field">
            <label className="smeadit-label">
              Affected Page <span className="smeadit-required">*</span>
            </label>
            <input
              name="pageAffected"
              value={form.pageAffected}
              onChange={handleChange}
              placeholder="e.g. Booking page, Contact form, Home"
              className={`smeadit-input ${errors.pageAffected ? 'smeadit-input--error' : ''}`}
            />
            {errors.pageAffected && <span className="smeadit-error">{errors.pageAffected}</span>}
          </div>
        </div>

        <div className="smeadit-form-grid">
          <div className="smeadit-field">
            <label className="smeadit-label">
              Priority <span className="smeadit-required">*</span>
            </label>
            <select
              name="priority"
              value={form.priority}
              onChange={handleChange}
              className={`smeadit-input ${errors.priority ? 'smeadit-input--error' : ''}`}
            >
              {PRIORITIES.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            {errors.priority && <span className="smeadit-error">{errors.priority}</span>}
          </div>

          <div className="smeadit-field">
            <label className="smeadit-label">Bug Type <span className="smeadit-optional">(optional)</span></label>
            <select name="bugType" value={form.bugType} onChange={handleChange} className="smeadit-input">
              {BUG_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="smeadit-field">
          <label className="smeadit-label">
            Description <span className="smeadit-required">*</span>
          </label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="What happened? What did you expect instead? Any steps to reproduce?"
            rows={5}
            className={`smeadit-input smeadit-textarea ${errors.description ? 'smeadit-input--error' : ''}`}
          />
          {errors.description && <span className="smeadit-error">{errors.description}</span>}
        </div>

        <div className="smeadit-field">
          <label className="smeadit-label">
            Screenshot <span className="smeadit-optional">(optional, max 10MB)</span>
          </label>

          {!screenshot ? (
            <label className="smeadit-upload-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>
              Attach a screenshot
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handleFile}
                hidden
              />
            </label>
          ) : (
            <div className="smeadit-screenshot-preview">
              <img src={screenshot.preview} alt="Screenshot preview" />
              <button type="button" className="smeadit-screenshot-remove" onClick={removeScreenshot} aria-label="Remove screenshot">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          )}

          {uploadProgress !== null && uploadProgress < 100 && (
            <div className="smeadit-progress">
              <div className="smeadit-progress-track">
                <div className="smeadit-progress-bar" style={{ width: `${uploadProgress}%` }} />
              </div>
              <span>{uploadProgress}%</span>
            </div>
          )}
          {errors.screenshot && <span className="smeadit-error">{errors.screenshot}</span>}
        </div>

        <div className="smeadit-form-footer">
          <button
            type="submit"
            className="smeadit-btn smeadit-btn--primary"
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Submit Bug Report'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SmeadITWidget;
